import { useState, useMemo, type ReactNode } from "react";
import { useUserContext } from "../context/UserContext";
import { useRetailer } from "../hooks/useRetailer";
import {
  AlertCircle,
  Bell,
  Trash2,
  RefreshCw,
  Package,
  MapPin,
  Store,
  Copy,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "../context/LanguageContext";
import LoadingState from "../components/LoadingState";
import ZipCodeModal from "../components/ZipCodeModal";
import {
  useTrackedProductsWithRetailer,
  useManuallyUpdateTrackerWithRetailer,
  useDeleteTrackersByProduct,
} from "../hooks/useTracking";
import {
  getTrackerLimit,
  type TrackedProduct,
} from "../services/TrackingService";
import { toast } from "react-toastify";
import { getProductImageUrl } from "../utils/imageUtils";

type FilterStatus = "all" | "ready" | "watching";
type Store = NonNullable<TrackedProduct["zephyrStores"]>[number];

const getStoresInStock = (t: TrackedProduct): Store[] =>
  (t.zephyrStores?.filter((s) => s.inventory > 0 && s.storePrice > 0) ?? [])
    .slice()
    .sort((a, b) => a.storePrice - b.storePrice);

const getLowestPrice = (t: TrackedProduct) => {
  const stores = getStoresInStock(t);
  const fromStore = stores.length ? stores[0].storePrice : Infinity;
  return Math.min(t.currentPrice ?? Infinity, fromStore);
};

const formatTime = (
  ts: number,
  t: (key: string, params?: Record<string, string | number>) => string,
) => {
  const mins = Math.floor((Date.now() - ts) / 60000);
  if (mins < 1) return t("alerts.justNow");
  if (mins < 60) return t("alerts.minutesAgo", { m: mins });
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return t("alerts.hoursAgo", { h: hrs });
  return t("alerts.daysAgo", { d: Math.floor(hrs / 24) });
};

export default function TracksPage() {
  const { user, isLoading: userLoading } = useUserContext();
  const retailer = useRetailer();
  const trackerLimit = getTrackerLimit(user?.accountType, user?.maxAlertsOverride);
  const { t } = useTranslation();

  const {
    data: trackers = [],
    isLoading: loading,
    isError,
    error,
    refetch,
  } = useTrackedProductsWithRetailer(retailer);

  const manuallyUpdateTrackerMutation =
    useManuallyUpdateTrackerWithRetailer(retailer);
  const deleteProductMutation = useDeleteTrackersByProduct(retailer);

  const [updatingTracker, setUpdatingTracker] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterStatus>("all");
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const [showZipCodeModal, setShowZipCodeModal] = useState(false);
  const [pendingUpdateUpc, setPendingUpdateUpc] = useState<string | null>(null);

  const reachedCount = trackers.filter(
    (tr) => getLowestPrice(tr) <= tr.targetPrice
  ).length;
  const watchingCount = trackers.length - reachedCount;

  const sortedTrackers = useMemo(
    () =>
      [...trackers].sort((a, b) => {
        const ar = getLowestPrice(a) <= a.targetPrice ? 1 : 0;
        const br = getLowestPrice(b) <= b.targetPrice ? 1 : 0;
        if (ar !== br) return br - ar;
        return b.createdAt - a.createdAt;
      }),
    [trackers]
  );

  const filteredTrackers = useMemo(() => {
    if (filter === "ready")
      return sortedTrackers.filter((tr) => getLowestPrice(tr) <= tr.targetPrice);
    if (filter === "watching")
      return sortedTrackers.filter((tr) => getLowestPrice(tr) > tr.targetPrice);
    return sortedTrackers;
  }, [sortedTrackers, filter]);

  const handleRemoveTracker = (upc: string) => {
    if (typeof window !== "undefined" && !window.confirm(t("alerts.stopTrackingConfirm"))) return;
    deleteProductMutation.mutate(upc, {
      onError: (err: any) => toast.error(err.message || t("alerts.removeFailed")),
    });
  };

  const handleManualUpdate = (upc: string) => {
    if (!user?.zipCode) {
      setPendingUpdateUpc(upc);
      setShowZipCodeModal(true);
      toast.info(t("products.setZipCodeToast"));
      return;
    }
    setUpdatingTracker(upc);
    manuallyUpdateTrackerMutation.mutate(upc, {
      onSettled: () => setUpdatingTracker(null),
    });
  };

  const handleZipCodeSuccess = (_zipCode: string) => {
    setShowZipCodeModal(false);
    if (pendingUpdateUpc) {
      setUpdatingTracker(pendingUpdateUpc);
      manuallyUpdateTrackerMutation.mutate(pendingUpdateUpc, {
        onSettled: () => {
          setUpdatingTracker(null);
          setPendingUpdateUpc(null);
        },
      });
    }
  };

  const handleCopy = async (value: string) => {
    await navigator.clipboard.writeText(value);
    toast.success(t("products.copiedLabel"));
  };

  const toggleExpand = (upc: string) =>
    setExpandedCards((prev) => {
      const next = new Set(prev);
      next.has(upc) ? next.delete(upc) : next.add(upc);
      return next;
    });

  if (userLoading || loading) return <LoadingState />;

  if (isError) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-gray-50 dark:bg-dark-950">
        <div className="text-center max-w-sm">
          <div className="w-14 h-14 rounded-2xl bg-red-50 dark:bg-red-900/20 flex items-center justify-center mx-auto mb-3.5">
            <AlertCircle className="w-7 h-7 text-red-500 dark:text-red-400" />
          </div>
          <p className="text-gray-900 dark:text-white font-bold mb-1">
            {t("products.failedToLoadAlerts")}
          </p>
          <p className="text-gray-500 dark:text-dark-400 text-sm mb-4">
            {error?.message}
          </p>
          <button
            onClick={() => refetch()}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-sm font-bold transition-colors"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            {t("products.tryAgainAction")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <ZipCodeModal
        isOpen={showZipCodeModal}
        onClose={() => {
          setShowZipCodeModal(false);
          setPendingUpdateUpc(null);
        }}
        onSuccess={handleZipCodeSuccess}
        currentZipCode={user?.zipCode || ""}
        title={t("products.zipCodeRequired")}
        description={t("products.zipCodeRequiredDesc")}
      />
      <div className="min-h-screen bg-gray-50/50 dark:bg-dark-950">
        <div className="max-w-3xl mx-auto px-4 pt-6 pb-24">
          <header className="flex items-baseline justify-between mb-3">
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-gray-950 dark:text-white [letter-spacing:-0.7px]">
              {t("products.priceAlerts")}
            </h1>
            {trackerLimit ? (
              <span className="text-sm font-bold text-gray-400 dark:text-dark-500 tabular-nums">
                {trackers.length}
                <span className="text-gray-400 dark:text-dark-500">
                  /{trackerLimit}
                </span>
              </span>
            ) : null}
          </header>

          {trackers.length > 0 && (
            <Segmented
              value={filter}
              onChange={setFilter}
              opts={[
                { id: "all", label: t("products.filterAll"), count: trackers.length },
                { id: "ready", label: t("products.filterReady"), count: reachedCount },
                { id: "watching", label: t("products.filterWatching"), count: watchingCount },
              ]}
            />
          )}

          {trackers.length === 0 ? (
            <AlertsHowtoEmpty seeDealsTo={`/${retailer}/products`} />
          ) : filteredTrackers.length === 0 ? (
            <EmptyState
              icon={<Package className="w-7 h-7 text-gray-400 dark:text-dark-400" />}
              title={
                filter === "ready"
                  ? t("products.noAlertsReady")
                  : t("products.noAlertsWatching")
              }
              action={
                <button
                  onClick={() => setFilter("all")}
                  className="text-sm font-bold text-primary-600 dark:text-primary-400 hover:underline mt-1"
                >
                  {t("products.viewAll")}
                </button>
              }
            />
          ) : (
            <div className="space-y-2.5">
              {filteredTrackers.map((tracker) => (
                <TrackerCard
                  key={tracker.upc}
                  tracker={tracker}
                  retailer={retailer}
                  isExpanded={expandedCards.has(tracker.upc)}
                  isUpdating={updatingTracker === tracker.upc}
                  onToggle={() => toggleExpand(tracker.upc)}
                  onUpdate={() => handleManualUpdate(tracker.upc)}
                  onDelete={() => handleRemoveTracker(tracker.upc)}
                  onCopy={() => handleCopy(tracker.upc)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ─── Segmented filter ─────────────────────────────────────────────────────────

interface SegmentedProps {
  value: FilterStatus;
  onChange: (v: FilterStatus) => void;
  opts: { id: FilterStatus; label: string; count: number }[];
}

function Segmented({ value, onChange, opts }: SegmentedProps) {
  return (
    <div className="flex gap-0.5 p-0.5 mb-3 rounded-lg bg-gray-100 dark:bg-white/5">
      {opts.map((o) => {
        const active = value === o.id;
        return (
          <button
            key={o.id}
            onClick={() => onChange(o.id)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md transition-colors text-xs ${
              active
                ? "bg-white dark:bg-dark-700 shadow-sm font-bold text-gray-900 dark:text-white"
                : "font-semibold text-gray-500 dark:text-dark-300 hover:text-gray-700 dark:hover:text-dark-100"
            }`}
          >
            {o.label}
            <span
              className={`min-w-[16px] px-1 rounded-full text-[10px] font-bold tabular-nums ${
                active
                  ? "bg-primary-100 dark:bg-primary-500/30 text-primary-700 dark:text-primary-200"
                  : "bg-gray-200/70 dark:bg-white/10 text-gray-500 dark:text-dark-400"
              }`}
            >
              {o.count}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────

function EmptyState({
  icon,
  title,
  desc,
  action,
  footer,
}: {
  icon: ReactNode;
  title: string;
  desc?: string;
  action?: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center text-center py-16 px-6">
      <div className="w-14 h-14 rounded-2xl bg-gray-100 dark:bg-white/5 flex items-center justify-center mb-3">
        {icon}
      </div>
      <h3 className="text-base font-bold text-gray-900 dark:text-white mb-1">
        {title}
      </h3>
      {desc && (
        <p className="text-sm text-gray-500 dark:text-dark-400 max-w-xs leading-relaxed">
          {desc}
        </p>
      )}
      {action}
      {footer}
    </div>
  );
}

// No alerts yet (pinto "Z2"): show the kind of alert they'll get, then where to set one.
function AlertsHowtoEmpty({ seeDealsTo }: { seeDealsTo: string }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center px-6 py-12 text-center" data-testid="alerts-howto-empty">
      <div className="mb-1.5 flex h-[76px] w-[76px] items-center justify-center rounded-full bg-primary-50 dark:bg-primary-500/15">
        <Bell className="h-[34px] w-[34px] fill-current text-primary-600 dark:text-purple-400" aria-hidden="true" />
      </div>
      <h3 className="mt-2 text-xl font-bold text-gray-900 dark:text-white">{t("emptyStates.alerts.title")}</h3>
      <p className="mt-1 max-w-xs text-sm leading-relaxed text-gray-500 dark:text-dark-400">{t("emptyStates.alerts.note")}</p>

      <div className="mt-3.5 flex w-full max-w-[340px] items-start gap-3 rounded-[20px] border border-gray-200 bg-white p-3 text-left shadow-sm dark:border-dark-700 dark:bg-dark-800">
        <img src="/app_icon.png" alt="" className="h-[38px] w-[38px] flex-none rounded-[10px]" />
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <div className="flex justify-between">
            <span className="text-[13.5px] font-bold text-gray-900 dark:text-white">DealHawk</span>
            <span className="text-[13px] text-gray-400 dark:text-dark-500">{t("emptyStates.alerts.sampleTime")}</span>
          </div>
          <p className="text-sm leading-[19px] text-gray-600 dark:text-dark-300">
            {t("emptyStates.alerts.sampleBefore")}{" "}
            <strong className="font-extrabold text-gray-900 dark:text-white">$187.00</strong>{" "}
            {t("emptyStates.alerts.sampleAfter")}
          </p>
        </div>
      </div>

      <div className="mt-3.5 flex flex-wrap items-center justify-center gap-1.5">
        <span className="text-sm font-semibold text-gray-600 dark:text-dark-300">{t("emptyStates.alerts.howtoInline")}</span>
        <span className="inline-flex items-center gap-1 rounded-[10px] bg-primary-50 px-2.5 py-1 text-primary-600 dark:bg-primary-500/15 dark:text-purple-400">
          <Bell className="h-[13px] w-[13px] fill-current" aria-hidden="true" />
          <span className="text-[13px] font-bold">{t("emptyStates.alerts.alertPill")}</span>
        </span>
      </div>

      <Link
        to={seeDealsTo}
        className="mt-5 inline-flex h-11 items-center justify-center rounded-full bg-primary-600 px-7 text-[15px] font-bold text-white shadow-sm shadow-primary-600/20 transition-colors hover:bg-primary-700"
      >
        {t("emptyStates.alerts.seeDeals")}
      </Link>
    </div>
  );
}

// ─── Tracker card ─────────────────────────────────────────────────────────────

interface TrackerCardProps {
  tracker: TrackedProduct;
  retailer: string;
  isExpanded: boolean;
  isUpdating: boolean;
  onToggle: () => void;
  onUpdate: () => void;
  onDelete: () => void;
  onCopy: () => void;
}

function TrackerCard({
  tracker,
  retailer,
  isExpanded,
  isUpdating,
  onToggle,
  onUpdate,
  onDelete,
  onCopy,
}: TrackerCardProps) {
  const { t } = useTranslation();
  const isWalmart = retailer === "walmart";
  const stores = getStoresInStock(tracker);
  const hasStores = stores.length > 0;
  const lowestStorePrice = hasStores ? stores[0].storePrice : Infinity;
  const onlinePrice = tracker.currentPrice ?? Infinity;
  const lowestPrice = Math.min(onlinePrice, lowestStorePrice);
  const isReached = isFinite(lowestPrice) && lowestPrice <= tracker.targetPrice;
  const isStorePrice = hasStores && lowestStorePrice < onlinePrice;
  const diff = isFinite(lowestPrice) ? lowestPrice - tracker.targetPrice : 0;

  return (
    <div className="bg-white dark:bg-[#221f1d] rounded-2xl border border-gray-200/80 dark:border-[#2e2a27] shadow-sm shadow-gray-900/[0.04] dark:shadow-none overflow-hidden">
      <div className="p-3.5">
        <div className="flex gap-3">
          <div className="w-[60px] h-[60px] rounded-xl flex-shrink-0 overflow-hidden bg-gray-100 dark:bg-[#1a1816] flex items-center justify-center">
            {tracker.image ? (
              <img
                src={getProductImageUrl(tracker.image, "200")}
                alt=""
                className="w-full h-full object-cover"
              />
            ) : (
              <Package className="w-5 h-5 text-gray-400 dark:text-dark-500" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-[13.5px] font-semibold leading-snug text-gray-900 dark:text-white line-clamp-2">
              {tracker.name}
            </h3>
            <div className="flex items-center flex-wrap gap-x-1.5 gap-y-1 mt-1.5 text-[11px] font-medium text-gray-500 dark:text-dark-300">
              <button
                onClick={onCopy}
                className="inline-flex items-center gap-1 hover:text-gray-700 dark:hover:text-dark-100 transition-colors"
              >
                <span className="font-mono">{tracker.upc}</span>
                <Copy className="w-2.5 h-2.5 text-gray-400 dark:text-dark-400" />
              </button>
              <Dot />
              <span className="inline-flex items-center gap-0.5">
                <MapPin className="w-2.5 h-2.5 text-gray-400 dark:text-dark-400" />
                {tracker.zipCode}
              </span>
              <Dot />
              <span className="text-gray-400 dark:text-dark-400">
                {formatTime(tracker.lastChecked, t)}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-baseline gap-2.5 flex-wrap mt-3">
          <span
            className={`text-2xl font-extrabold tabular-nums [letter-spacing:-0.6px] ${
              isReached
                ? "text-emerald-600 dark:text-emerald-400"
                : "text-gray-900 dark:text-white"
            }`}
          >
            {isFinite(lowestPrice) ? `$${lowestPrice.toFixed(2)}` : "—"}
          </span>
          <div className="flex items-center flex-wrap gap-1.5 min-w-0">
            {isStorePrice && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-blue-50 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300 text-[10.5px] font-bold tracking-wide">
                <Store className="w-2.5 h-2.5" />
                {t("products.inStoreLabel")}
              </span>
            )}
            <span className="text-[12.5px] font-medium tabular-nums text-gray-400 dark:text-dark-400">
              {t("products.targetLabel")} ${tracker.targetPrice.toFixed(2)}
            </span>
            {isReached ? (
              <span className="text-[12.5px] font-bold tabular-nums text-emerald-600 dark:text-emerald-400">
                · {t("products.filterReady")}
              </span>
            ) : isFinite(lowestPrice) && diff > 0 ? (
              <span className="text-[12.5px] font-bold tabular-nums text-amber-600 dark:text-amber-400">
                · ${diff.toFixed(2)} over
              </span>
            ) : null}
          </div>
        </div>
        {tracker.rules?.mode && tracker.rules.mode !== "price" && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            <span className="rounded-full bg-violet-50 dark:bg-violet-900/25 px-2 py-1 text-[10px] font-black uppercase tracking-wider text-violet-700 dark:text-violet-300">{tracker.rules.mode} watch</span>
            {!!tracker.rules.minDiscount && <span className="rounded-full bg-gray-100 dark:bg-dark-700 px-2 py-1 text-[10px] font-bold text-gray-600 dark:text-dark-200">{tracker.rules.minDiscount}%+ off</span>}
            {!!tracker.rules.minProfit && <span className="rounded-full bg-emerald-50 dark:bg-emerald-900/25 px-2 py-1 text-[10px] font-bold text-emerald-700 dark:text-emerald-300">${tracker.rules.minProfit}+ profit</span>}
            {!!tracker.rules.minRoi && <span className="rounded-full bg-emerald-50 dark:bg-emerald-900/25 px-2 py-1 text-[10px] font-bold text-emerald-700 dark:text-emerald-300">{tracker.rules.minRoi}%+ ROI</span>}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between px-3.5 py-2.5 border-t border-gray-100 dark:border-[#2a2724]">
        {hasStores ? (
          <button
            onClick={onToggle}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-500 dark:text-dark-300 hover:text-gray-700 dark:hover:text-dark-100 transition-colors"
          >
            <Store className="w-3.5 h-3.5" />
            {t("products.storesInStock", { count: String(stores.length) })}
            {isExpanded ? (
              <ChevronUp className="w-3.5 h-3.5 text-gray-400 dark:text-dark-400" />
            ) : (
              <ChevronDown className="w-3.5 h-3.5 text-gray-400 dark:text-dark-400" />
            )}
          </button>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-400 dark:text-dark-500">
            <AlertCircle className="w-3.5 h-3.5" />
            {t("products.outOfStockLabel")}
          </span>
        )}
        <div className="flex items-center gap-0.5">
          <button
            onClick={onUpdate}
            disabled={isUpdating}
            className="p-1.5 rounded-md text-gray-400 dark:text-dark-400 hover:text-gray-600 dark:hover:text-dark-100 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors disabled:opacity-50"
            title={t("products.refreshLabel")}
          >
            <RefreshCw className={`w-4 h-4 ${isUpdating ? "animate-spin" : ""}`} />
          </button>
          <button
            onClick={onDelete}
            className="p-1.5 rounded-md text-gray-400 dark:text-dark-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
            title={t("products.deleteLabel")}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {isExpanded && hasStores && (
        <StoreList stores={stores} isWalmart={isWalmart} />
      )}
    </div>
  );
}

// ─── Store list ───────────────────────────────────────────────────────────────

function StoreList({ stores, isWalmart }: { stores: Store[]; isWalmart: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="bg-gray-50 dark:bg-white/[0.03] border-t border-gray-100 dark:border-[#2a2724] divide-y divide-gray-100 dark:divide-[#2a2724]">
      {stores.map((s) => (
        <div key={s.storeNbr} className="px-3.5 py-3">
          <div className="flex items-start gap-2">
            <div className="flex-1 min-w-0">
              <div className="text-[12.5px] font-bold text-gray-900 dark:text-white">
                {t("products.storeNumber", { id: s.storeNbr })}
              </div>
              <div className="text-[11.5px] text-gray-500 dark:text-dark-300 truncate">
                {s.storeAddress}
              </div>
            </div>
            <div className="text-right flex-shrink-0">
              <div className="text-sm font-bold tabular-nums text-gray-900 dark:text-white">
                ${Number(s.storePrice).toFixed(2)}
              </div>
              <div className="text-[10.5px] font-semibold text-emerald-600 dark:text-emerald-400">
                {s.inventory} {t("products.inStock").toLowerCase()}
              </div>
            </div>
          </div>
          {(isWalmart || s.aisleLocation) && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {isWalmart && <Chip>{t("products.floorLabel")} {s.inventory}</Chip>}
              {isWalmart && <Chip>{t("products.backLabel")} {s.backroom || 0}</Chip>}
              {s.aisleLocation && <Chip>{s.aisleLocation}</Chip>}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

const Chip = ({ children }: { children: ReactNode }) => (
  <span className="px-2 py-0.5 rounded-md border border-gray-200 dark:border-white/10 bg-white dark:bg-white/[0.04] text-[10.5px] font-semibold text-gray-600 dark:text-dark-200">
    {children}
  </span>
);

const Dot = () => (
  <span className="inline-block w-[3px] h-[3px] rounded-full bg-gray-300 dark:bg-dark-500" />
);
